import { TextLinkCaret } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  getCrowdDensityWidgetMatrixData,
  getDwellTimeWidgetMatrixData,
  toMin,
  getEncryptedHash,
  getIcon,
  getInOutCountForEEAppsWidgetMatrixData,
  getParkingOccupancyOAWidgetMatrixData,
  getParkingOccupancyWidgetMatrixData,
  getWidgetDisplayName,
  getWidgetMatrixData,
  isWidgetOfEventApp,
  getWidgetDataForEventApps,
  getTimeLabelWithTimezone,
  getStatOaMaxAvgOfWidgetData
} from "../../global/utility/statisticsUtil";
import { TextLinkCaretButton } from "../Dashboard/styles";
import KnowiChart from "./charts";
import "./charts.css";
import ApacheCharts from "./ApacheCharts";
import { getAggregatedDataForEntryExitCVApp } from "../../api/EbcWidgetStatisticManager";
import { getAggregateFor, getAggregateForEntryExitApp } from "../../model/reportService";
import { complete_widgetList, 
  multiLineChartColorList,
  eventChartBlue,
  eventChartOrange,
statDarkBlue,
xAXIS_TOOLTIP_UNIT_IN_MINUTE,
xAXIS_TOOLTIP_UNIT_IN_PERCENT,
CHART_TYPES} from "./statisticsConstants";
import moment from "moment";
import { Body } from "@vds/typography";
import { getLocationHierarchy } from "../../global/utility/widgetUtils";

const parseSpaceDelimitedToken = (text, index, parser) =>
  text.includes(" ") ? parser(text.split(" ")[index]) : 0;

const EMPTY_LIST = [];

const WidgetComponent = ({ widgetList,
  showHideFilter, 
  encryptPayload, 
  aggregatedCVAppCount, 
  selectedCVAppName, 
  renderedFrom, 
  cvAppImageName,
  extendedstatus = false,
  stateCurrentValue = "" }) => {
  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const cvAppNames = useSelector((state) => state.cvAppReducer.cvAppList);
   const sitesTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const multiLineColorList = multiLineChartColorList;
  const statisticsCharts = useSelector((state) => state.userAppSettingsReducer.StatisticsCharts);
  const isDataMatrixFunctinalityEnabled = window.REACT_APP_STATS_DATA_MATRIX_ENABLED;
  // Same field values from the parent still arrive as a new object reference (spread updates
  // upstream), which would re-trigger the [encryptParams, ...] effect below on every no-op update.
  // Keying the memo off the serialized content keeps the reference stable when nothing changed.
  const encryptParams = useMemo(() => encryptPayload, [JSON.stringify(encryptPayload)]);
  const safeAppImageName = cvAppImageName || selectedCVAppName || "";
  const [encryptedHash, setEncryptedHash] = useState("");
  const [dataMatrixState, setDataMatrix] = useState([]);
  const [dataMatrixForEEApps, setDataMatrixForEEApps] = useState([]);
  const [dataMatrixForOAApps, setDataMatrixForOAApps] = useState([]);
  const [eventInfoResponse, setEventInfoResponse] = useState([]);
  const  [chartDataSetsForOA, setChartDataSetsForOA] = useState([]);
  const  [chartDataSetsForEE, setChartDataSetsForEE] = useState([]);

  const  [chartDataSetsState, setChartDataSets] = useState([]);
  // Params the current chart/matrix state belongs to. Until the reset effect catches up with a new
  // encryptParams, the previous app's data is hidden instead of flashing under the new widget list.
  const [dataParams, setDataParams] = useState(null);
  const justReset = useRef(false);
  const dataIsCurrent = dataParams === encryptParams;
  const dataMatrix = dataIsCurrent ? dataMatrixState : EMPTY_LIST;
  const chartDataSets = dataIsCurrent ? chartDataSetsState : EMPTY_LIST;
  const [widgetError, setWidgetError] = useState(null);
  const EventWidgetBlue = eventChartBlue;
  const EventWidgetOrange = eventChartOrange;
  const StatWidgetDarkBlue = statDarkBlue


  const fullWidgetList = complete_widgetList;

  function hasEEAppWidget(listOfWidgets) {
    return (
      listOfWidgets.filter((h) => {
        return h?.name.indexOf("_ee_") > -1;
      }).length > 0
    );
  }

  function hasOAAppWidget(listOfWidgets) {
    return (
      listOfWidgets.filter((h) => {
        return h?.name.indexOf("_oa_") > -1 || h?.name.indexOf("_fa_") > -1;
      }).length > 0
    );
  }

  const hasUndefinedValues = (obj) => {
    return Object.values(obj).some(value => value === undefined);
  };

   const axixDateformat = (m) => {
      const parsed = moment(m);
      return parsed.isValid() ? parsed.format(encryptParams?.xAxisDateFormat || "hh:mmA") : "";
    }

   const axixtooltipFormat = (m) => {
      const parsed = moment(m);
      return parsed.isValid() ? parsed.format("MM/DD/YYYY hh:mmA") : null;
    }

  useEffect(() => {
    justReset.current = true;
    setDataParams(encryptParams);
    setChartDataSets([]);
    setChartDataSetsForOA([]);
    setChartDataSetsForEE([]);
    setDataMatrixForOAApps([]);
    setDataMatrixForEEApps([]);
    setDataMatrix([]);
    setWidgetError(null);
    if (hasUndefinedValues(encryptParams)) {
      return; // Exit early if validation fails
    }

    let contentFilter = [
      {
        fieldName: "$c9_token$",
        values: [activeSession.sessionToken],
        operator: "="
      },
      {
        fieldName: "$c9_accountname$",
        values: [encryptParams?.accountname],
        operator: "="
      },
      {
        fieldName: "$c9_startDate$",
        values: [encryptParams?.startDate],
        operator: "="
      },
      {
        fieldName: "$c9_endDate$",
        values: [encryptParams?.endDate],
        operator: "="
      },
      {
        fieldName: "$c9_transactionid$",
        values: [uuidv4()],
        operator: "="
      },
      {
        fieldName: "$c9_tagid$",
        values: [encryptParams?.tagid],
        operator: "="
      },
      {
        fieldName: "$c9_fixedinterval$",
        values: [encryptParams?.histogram_fixedinterval],
        operator: "="
      },
      {
        fieldName: "$c9_getTagsKind$",
        values: [encryptParams?.getTagsKind],
        operator: "="
      },
      {
        fieldName: "$c9_xAxisDateFormat$",
        values: [encryptParams?.xAxisDateFormat],
        operator: "="
      },
      {
        fieldName: "$c9_eventfield2$",
        values: [encryptParams?.eventfield],
        operator: "="
      },
      {
        fieldName: "$c9_eventfieldForPriority$",
        values: [encryptParams?.eventfieldForPriority],
        operator: "="
      },
      {
        fieldName: "$c9_objectClass$",
        values: [encryptParams?.objectClass ?? ""],
        operator: "="
      },
      {
        fieldName: "$c9_eeFixedInterval$",
        values: [encryptParams?.eeFixedInterval ?? ""],
        operator: "="
      },
      {
        fieldName: "$c9_extendedendDate$",
        values: [encryptParams?.extendedendDate ?? ""],
        operator: "="
      },
      {
        fieldName: "$c9_tagIdList$",
        values: [encryptParams?.tagIdList ?? ""],
        operator: "="
      }
    ];
    if (statisticsCharts?.chart === CHART_TYPES.KNOWI) {
      getEncryptedHashForWidget(contentFilter);
    }
    invokeMethodforDataMatrix(encryptParams);
  }, [encryptParams, statisticsCharts?.chart]);

  async function invokeMethodforDataMatrix(encryptParams) {
      if (isWidgetOfEventApp(cvAppNames, selectedCVAppName)) {
        if(encryptParams?.eventfieldForPriority.split(".")[1] != "undefined"){
        getWidgetMatrixDataDispaly();
        }
      } else if (encryptParams?.eventfieldForPriority.split(".")[1].indexOf("Parking") > -1) {
        setDataMatrix([]);
        if (hasEEAppWidget(widgetList)&& encryptParams?.tagIdListEE !== "") {
          getParkingOccupancyWidgetMatrixDataDispaly("ParkingBayOccupancyEE");
        }
        if (hasOAAppWidget(widgetList) && encryptParams?.tagIdListOA !== "") {
          getParkingOccupancyOAWidgetMatrixDataDispaly("ParkingBayOccupancy");
        }
      } else if (encryptParams?.eventfieldForPriority.split(".")[1].indexOf("People") > -1) {
        setDataMatrix([]);

        if (hasEEAppWidget(widgetList) && encryptParams?.tagIdListEE !== "") {
          await getPeopleCountEEWidgetMatrixDataDispaly();
        }
        if (hasOAAppWidget(widgetList) && encryptParams?.tagIdListOA !== "") {
          await getPeopleCountOAWidgetMatrixDataDispaly("PeopleCountOA");
        }
      } else if (encryptParams?.eventfieldForPriority.split(".")[1].indexOf("Vehicle") > -1) {
        setDataMatrix([]);
        if (hasEEAppWidget(widgetList) && encryptParams?.tagIdListEE !== "") {
          getPeopleCountEEWidgetMatrixDataDispaly();
        }
        if (hasOAAppWidget(widgetList) && encryptParams?.tagIdListOA !== "") {
          getPeopleCountOAWidgetMatrixDataDispaly("VehicleCountOA");
        }
      } else if (encryptParams?.eventfieldForPriority.split(".")[1].indexOf("Crowd") > -1) {
        getCrowdDensityWidgetMatrixDataDispaly();
      } else if (encryptParams?.eventfieldForPriority.split(".")[1].indexOf("Dwell") > -1) {
        getDwellTimeWidgetMatrixDataDisplay();
      }else {
        setDataMatrix([]);
      }
  }

  useEffect(() => {
    // Skip the run that shares a commit with the reset effect: its closure still holds the previous app's data.
    if (justReset.current) return;
    let appnameForDatamatrix = encryptParams?.eventfieldForPriority.split(".")[1];
    if (appnameForDatamatrix.indexOf("Parking") > -1 || appnameForDatamatrix.indexOf("People") > -1 || appnameForDatamatrix.indexOf("Vehicle") > -1) {
      let finalParkingdatamatrixList = [];
      let finalChartDataSet = [];
      if (!hasEEAppWidget(widgetList)) {
        setDataMatrix(dataMatrixForOAApps);
        setChartDataSets(chartDataSetsForOA);
      } else if (!hasOAAppWidget(widgetList)) {
        setDataMatrix(dataMatrixForEEApps);
        setChartDataSets(chartDataSetsForEE);
      } else if (hasEEAppWidget(widgetList) && hasOAAppWidget(widgetList)) {
        const dtas = dataMatrixForEEApps;
        const dtasSet = chartDataSetsForEE;
        finalParkingdatamatrixList = [];
        dtas.forEach((element, i) => {
          finalParkingdatamatrixList.push(element);
          finalChartDataSet.push(chartDataSetsForEE[i]);
          if (dataMatrixForOAApps.length > 0) {
            const isParkingApp = appnameForDatamatrix.indexOf("Parking") > -1;
            if ((i !== 1 && !isParkingApp)) {
              finalChartDataSet.push(chartDataSetsForOA[i == 0 ? 0 : i - 1]);
              finalParkingdatamatrixList.push(dataMatrixForOAApps[i == 0 ? 0 : i - 1]);
              
            }else if(isParkingApp){
              finalChartDataSet.push(chartDataSetsForOA[i]);
              finalParkingdatamatrixList.push(dataMatrixForOAApps[i]);
            }
          }
        });
        setDataMatrix(finalParkingdatamatrixList);
        setChartDataSets(finalChartDataSet);
      }
    }
  }, [widgetList, dataMatrixForOAApps, dataMatrixForEEApps]);

  useEffect(() => {
    justReset.current = false;
  });
  async function getEncryptedHashForWidget(contentFilter) {
    try {
      const fetchIncidentsRsp = await getEncryptedHash(contentFilter);
      if (fetchIncidentsRsp?.error) {
        setWidgetError("Failed to load widget. Please try again.");
        return;
      }
      const status = fetchIncidentsRsp?.status;
      if (status == 401) {
        //navigate("/login");
      }
      setEncryptedHash(fetchIncidentsRsp?.encryptedContent);
    } catch (err) {
      setWidgetError("Failed to load widget. Please try again.");
    }
  }

  async function getWidgetMatrixDataDispaly() {
    try {
    const timeLabel = getTimeLabelWithTimezone();

    let contentFilter = {
      accountname: encryptParams?.accountname,
      tagid: encryptParams?.tagid,
      fixedinterval: encryptParams?.histogram_fixedinterval,
      eventfield2: encryptParams?.eventfieldForPriority,
      startDate: encryptParams?.startDate,
      endDate: encryptParams?.endDate,
      extendedendDate: encryptParams?.extendedendDate,
      resolveStatus: "Unresolved",
      aggregateKind: "countininterval",
      objectClass: encryptParams?.objectClass ?? "",
      tagList: encryptParams?.tagIdList
    };
    const fetchIncidentsRsp = await getWidgetDataForEventApps(contentFilter);
    if (fetchIncidentsRsp?.error) {
      setWidgetError("Failed to load event widget data. Please try again.");
      return;
    }
    if (!fetchIncidentsRsp?.error) {
      let objDaataMatrix = fetchIncidentsRsp?.datamatrix.map((m) => {
        return m.EventName_OvertIme;
      });
      

      // Sum CountHigh across all locations per time bucket in a single linear pass.
      let charDataValuesHigh = (fetchIncidentsRsp?.apiresponse ?? []).reduce(
        (sums, m) => m.response_Unresolved.map((j, l) => j.CountHigh ? j.CountHigh + (sums[l] ? sums[l] : 0) : sums[l]),
        []
      );
      
      // Sum CountLow across all locations per time bucket in a single linear pass.
      let charDataValuesLow = (fetchIncidentsRsp?.apiresponse ?? []).reduce(
        (sums, m) => m.response_Unresolved.map((j, l) => j.CountLow ? j.CountLow + (sums[l] ? sums[l] : 0) : sums[l]),
        []
      );

      // Average averageTimeHigh across all locations per time bucket (mean of per-location averages).
      let charDataValuesHighResolute = (fetchIncidentsRsp?.apiresponse ?? []).reduce(
        (sums, m) => m.response_Resolved.map((j, l) => j.averageTimeHigh ? parseFloat((j.averageTimeHigh/60000).toFixed(2)) + (sums[l] ? sums[l] : 0) : sums[l]),
        []
      );
      
      // Sum averageTimeLow across all locations per time bucket in a single linear pass.
      let charDataValuesLowResolute = (fetchIncidentsRsp?.apiresponse ?? []).reduce(
        (sums, m) => m.response_Resolved.map((j, l) => j.averageTimeLow ? parseFloat((j.averageTimeLow/60000).toFixed(2))+ (sums[l] ? sums[l] : 0) : sums[l]),
        []
      );

      const uniqueUnresolvedDateTimes = [...new Set(fetchIncidentsRsp?.apiresponse.flatMap((m) => m.response_Unresolved.map((j) => j.dateTime)))];
      let chartSeries = {
        axisData: {"xName": timeLabel, "yName":"Event Count",
          "data": uniqueUnresolvedDateTimes.map(axixDateformat),
          "tooltipData": uniqueUnresolvedDateTimes.map(axixtooltipFormat)},
        seriesValue: [
          {
            name: "Minor",
            data: charDataValuesLow,
            color: EventWidgetBlue
          },
          {
            name: "Critical",
            data: charDataValuesHigh,
            color: EventWidgetOrange
          }
        ]
      };
      const uniqueResolvedDateTimes = [...new Set(fetchIncidentsRsp?.apiresponse.flatMap((m) => m.response_Resolved.map((j) => j.dateTime)))];
      let chartSeriesResolute = {
        axisData: {"xName": timeLabel, "yName":"Resolution Time", "toolTipUnit": xAXIS_TOOLTIP_UNIT_IN_MINUTE,
          "data": uniqueResolvedDateTimes.map(axixDateformat),
          "tooltipData": uniqueResolvedDateTimes.map(axixtooltipFormat)},
        seriesValue: [
          {
            name: "Minor",
            data: charDataValuesLowResolute,
            color: EventWidgetBlue
          },
          {
            name: "Critical",
            data: charDataValuesHighResolute,
            color: EventWidgetOrange
          }
        ]
      };
      let chartBarSeries = {
        axisData: {"xName": "", "yName":"Event Count",
          "data": fetchIncidentsRsp?.apiresponse.map((m) => m.response_UnResolvedByLoc?.locationName || "name1"),
          "locationIdData": fetchIncidentsRsp?.apiresponse.map((m) => m.response_UnResolvedByLoc?.locationId)},
        seriesValue: [
          {
            name: "Minor",
            data: fetchIncidentsRsp?.apiresponse.map((m) => m.response_UnResolvedByLoc?.minor),
            color: EventWidgetBlue
          },
          {
            name: "Critical",
            data: fetchIncidentsRsp?.apiresponse.map((m) => m.response_UnResolvedByLoc?.critical),
            color: EventWidgetOrange
          }
        ]
      };

      let chartBarSeriesResolute = {
        axisData: {"xName": "", "yName":"Resolution Time", "toolTipUnit": xAXIS_TOOLTIP_UNIT_IN_MINUTE,
          "data": fetchIncidentsRsp?.apiresponse.map((m) => m.response_ResolvedByLoc?.locationName || "name1"),
          "locationIdData": fetchIncidentsRsp?.apiresponse.map((m) => m.response_ResolvedByLoc?.locationId)},
        seriesValue: [
          {
            name: "Minor",
            data: fetchIncidentsRsp?.apiresponse.map((m) => parseFloat(m.response_ResolvedByLoc?.minor/60000).toFixed(2)),
            color: EventWidgetBlue
          },
          {
            name: "Critical",
            data: fetchIncidentsRsp?.apiresponse.map((m) => parseFloat(m.response_ResolvedByLoc?.critical/60000).toFixed(2)),
            color: EventWidgetOrange
          }
        ]
      };
      setChartDataSets([chartSeries, chartBarSeries, chartSeriesResolute, chartBarSeriesResolute]);
      setDataMatrix(objDaataMatrix);
    }else{
      setDataMatrix([]);
    }
    } catch (err) {
      setWidgetError("Failed to load event widget data. Please try again.");
    }
  }
  async function getParkingOccupancyWidgetMatrixDataDispaly() {
    try {
    const timeLabel = getTimeLabelWithTimezone();

    let contentFilter = {
      accountname: encryptParams?.accountname,
      tagid: encryptParams?.tagid,
      fixedinterval: encryptParams?.histogram_fixedinterval,
      appName: encryptParams?.eventfieldForPriority.split(".")[1],
      startDate: encryptParams?.startDate,
      endDate: encryptParams?.endDate,
      extendedendDate: encryptParams?.extendedendDate,
      aggregation: "latestininterval",
      tagList: encryptParams?.tagIdListEE
    };
    let fetchIncidentsRsp;
    try {
      fetchIncidentsRsp = await getParkingOccupancyWidgetMatrixData(contentFilter);
    } catch (err) {
      setWidgetError("Failed to load people count data. Please try again.");
      return;
    }
    if (fetchIncidentsRsp?.error) {
      setWidgetError("Failed to load parking occupancy data. Please try again.");
      return;
    }
    if (!fetchIncidentsRsp?.error) {
    let totalParkingCapacityOfAllLocations = fetchIncidentsRsp?.apiresponse.reduce((sum, outcome) => sum + parseInt(outcome.locationParkingCapacity), 0);

       let avegaeoccupancy =
        fetchIncidentsRsp.finalDataOccupiedList.reduce((accumulator, currentValue) => accumulator + currentValue, 0) /
        fetchIncidentsRsp.finalDataOccupiedList.filter(f => f > 0).length;

      avegaeoccupancy = avegaeoccupancy == 0 || avegaeoccupancy == null || isNaN(avegaeoccupancy) ? 0 : parseInt(avegaeoccupancy);

      let maxpercentage = parseInt((Math.max(...fetchIncidentsRsp.finalDataOccupiedList) / totalParkingCapacityOfAllLocations) * 100);
      let avgPercentage = parseInt((avegaeoccupancy / totalParkingCapacityOfAllLocations) * 100);
      
      maxpercentage = isNaN(maxpercentage) ? 0 : maxpercentage > 100 ? 100 : maxpercentage;
      avgPercentage = isNaN(avgPercentage) ? 0 : avgPercentage > 100 ? 100 : avgPercentage;
      

      let parkingDataMatrix = [];
      parkingDataMatrix.push([
        { key: "Peak Occupancy", count: Math.max(...fetchIncidentsRsp.finalDataOccupiedList) }
      ]);
      parkingDataMatrix.push([
        { key: "Average Occupancy", count: parseInt(avgPercentage) + "%" },
        { key: "Max Occupancy", count: parseInt(maxpercentage) + "%" }
      ]);
      parkingDataMatrix.push([]);
      parkingDataMatrix.push([{ key: "Average Occupancy", count: parseInt(avgPercentage) + "%" }]);
      
       let chartUniLineSeries = {
        axisData: {"xName": timeLabel, "yName":safeAppImageName,
          "data": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: [
          {
            name:  safeAppImageName ,
            data: fetchIncidentsRsp?.finalDataOccupiedList,
            color: EventWidgetBlue
          }
        ]
      };

      if(renderedFrom === "EventInfo"){
        chartUniLineSeries.axisData.markerPoint = true;
        if(extendedstatus){
          const statValueNumber = parseSpaceDelimitedToken(stateCurrentValue, 0, parseInt);
          const data = chartUniLineSeries.seriesValue[0].data;
          data[data.length - 1] = statValueNumber;
          if(chartUniLineSeries.seriesValue.length > 1){
            const data2 = chartUniLineSeries.seriesValue[1].data;
            data2[data2.length - 1] = 100 - statValueNumber;
          }
        }
        setChartDataSetsForEE([chartUniLineSeries]);
        setChartDataSets([chartUniLineSeries]);
        return;
      }

      contentFilter.fixedinterval = encryptParams?.eeFixedInterval;
       const fetchIncidentsRsp2 = await getParkingOccupancyWidgetMatrixData(contentFilter);
      let chartBarSeries = {
        axisData: {"xName": timeLabel, "yName":selectedCVAppName ,"toolTipUnit": xAXIS_TOOLTIP_UNIT_IN_PERCENT,
          "data": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: [
          {
            name: "Parking Occupancy",
            data: fetchIncidentsRsp2?.finalDataOccupiedList.map(m => parseInt((m/parseInt(totalParkingCapacityOfAllLocations)) * 100)) ,
            color: EventWidgetBlue
          },
          {
            name: "Parking Available",
            data: fetchIncidentsRsp2?.finalDataOccupiedList.map(m => 100 - parseInt((m/parseInt(totalParkingCapacityOfAllLocations)) * 100)) ,
            color: StatWidgetDarkBlue
          }
        ]
      };
      if(encryptParams.tagIdList.split(",").length > 0){
        let chartMultiLineSeries = {
        axisData: {"xName": timeLabel, "yName":selectedCVAppName,
          "data": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: fetchIncidentsRsp?.apiresponse.map((outcome, i) => { return {
            "name": outcome?.locationName,
            "locationId": outcome?.locationId,
            "data": outcome?.ParkingAvailable_Outcome.map(m => m.value.latest.value),
            "color": multiLineColorList[i]
          }
        })
      };
       if (!fetchIncidentsRsp2?.error) {
        let chartHeatMatrixSeries = {
        axisData: {"xName": timeLabel, "yName":"",
          "data": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: fetchIncidentsRsp2?.apiresponse.map((outcome, i) => { return {
            "name": outcome?.locationName,
            "locationId": outcome?.locationId,
            "data": outcome?.ParkingAvailable_Outcome.map(m => m.value.latest.value),
          }
        })
      };
      setChartDataSetsForEE([chartUniLineSeries,chartBarSeries,chartHeatMatrixSeries,chartMultiLineSeries])
      
       }
      }else{
      setChartDataSetsForEE([chartUniLineSeries, chartBarSeries])
      }

      setDataMatrixForEEApps(parkingDataMatrix);
      
    }
    } catch (err) {
      setWidgetError("Failed to load parking occupancy data. Please try again.");
    }
  }

  async function getParkingOccupancyOAWidgetMatrixDataDispaly(cvAppname) {
    const timeLabel = getTimeLabelWithTimezone();

    let contentFilter = {
      accountname: encryptParams?.accountname,
      tagid: encryptParams?.tagid,
      fixedinterval: encryptParams?.histogram_fixedinterval,
      appName: "ParkingBayOccupancy",
      startDate: encryptParams?.startDate,
      endDate: encryptParams?.endDate,
      extendedendDate: encryptParams?.extendedendDate,
      aggregation: "sumwindowininterval",
      tagList: encryptParams?.tagIdListOA
    };
    let fetchIncidentsRsp;
    try {
      fetchIncidentsRsp = await getCrowdDensityWidgetMatrixData(contentFilter);
    } catch (err) {
      setWidgetError("Failed to load parking occupancy data. Please try again.");
      return;
    }
    if (fetchIncidentsRsp?.error) {
      setWidgetError("Failed to load parking occupancy data. Please try again.");
      return;
    }

    const totalCapacity = fetchIncidentsRsp.apiresponse.reduce(
      (sum, outcome) => sum + parseInt(outcome.locationParkingCapacity), 0
    );
    const nonZeroItems = fetchIncidentsRsp.itemlist.filter(f => f > 0);
    const avgOccupancy = nonZeroItems.length > 0
      ? parseInt(nonZeroItems.reduce((acc, v) => acc + v, 0) / nonZeroItems.length)
      : 0;
    const clamp = (val) => isNaN(val) ? 0 : Math.min(100, val);
    const maxPercentage = clamp(parseInt((Math.max(...fetchIncidentsRsp.itemlist) / totalCapacity) * 100));
    const avgPercentage = clamp(parseInt((avgOccupancy / totalCapacity) * 100));

    const parkingDataMatrix = [
      [{ key: "Peak Occupancy", count: Math.max(...fetchIncidentsRsp.itemlist) }],
      [{ key: "Average Occupancy", count: `${avgPercentage}%` }, { key: "Max Occupancy", count: `${maxPercentage}%` }],
      [],
      [{ key: "Average Occupancy", count: `${avgPercentage}%` }],
    ];

    const firstOutcomeTimes = fetchIncidentsRsp.apiresponse[0]?.ParkingAvailable_Outcome ?? [];
    const chartUniLineSeries = {
      axisData: {
        xName: timeLabel, yName: safeAppImageName, toolTipUnit: "Occupancy", additionalToolTip: totalCapacity,
        data: firstOutcomeTimes.map(m => axixDateformat(m.key)),
        tooltipData: firstOutcomeTimes.map(m => axixtooltipFormat(m.key)),
      },
      seriesValue: [{ name: safeAppImageName, data: fetchIncidentsRsp.itemlist, color: EventWidgetBlue }],
    };

      if(renderedFrom === "EventInfo"){
        chartUniLineSeries.axisData.markerPoint = true;
        if(extendedstatus){
          const statValueNumber = parseSpaceDelimitedToken(stateCurrentValue, 0, parseInt);
          const data = chartUniLineSeries.seriesValue[0].data;
          data[data.length - 1] = statValueNumber;
        }
        setChartDataSetsForOA([chartUniLineSeries]);
        setChartDataSets([chartUniLineSeries]);
        return;
      }
      
    let fetchIncidentsRsp2;
    try {
      fetchIncidentsRsp2 = await getCrowdDensityWidgetMatrixData({ ...contentFilter, fixedinterval: encryptParams?.eeFixedInterval });
    } catch (err) {
      setChartDataSetsForOA([chartUniLineSeries]);
      setDataMatrixForOAApps(parkingDataMatrix);
      return;
    }

    const toPercent = (val, capacity) => parseInt((val / capacity) * 100);
    const rsp2FirstTimes = fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome ?? [];
    const chartBarSeries = {
      axisData: {
        xName: timeLabel, yName: cvAppname ? selectedCVAppName : "Crowd Count", toolTipUnit: xAXIS_TOOLTIP_UNIT_IN_PERCENT,
        data: rsp2FirstTimes.map(m => axixDateformat(m.key)),
        tooltipData: rsp2FirstTimes.map(m => axixtooltipFormat(m.key)),
      },
      seriesValue: [
        { name: "Parking Occupancy", data: fetchIncidentsRsp2?.itemlist.map(m => toPercent(m, totalCapacity)), color: EventWidgetBlue },
        { name: "Parking Available", data: fetchIncidentsRsp2?.itemlist.map(m => 100 - toPercent(m, totalCapacity)), color: StatWidgetDarkBlue },
      ],
    };

    const hasMultipleTags = encryptParams.tagIdList.split(",").length > 1;
    if (hasMultipleTags) {
      const chartMultiLineSeries = {
        axisData: {
          xName: timeLabel, yName: cvAppname ? selectedCVAppName : "Crowd Count", toolTipUnit: xAXIS_TOOLTIP_UNIT_IN_PERCENT,
          data: firstOutcomeTimes.map(m => axixDateformat(m.key)),
          tooltipData: firstOutcomeTimes.map(m => axixtooltipFormat(m.key)),
        },
        seriesValue: fetchIncidentsRsp.apiresponse.map((outcome, i) => ({
          name: outcome?.locationName,
          locationId: outcome?.locationId,
          data: outcome?.ParkingAvailable_Outcome.map(m => toPercent(m.value, parseInt(outcome.locationParkingCapacity))),
          color: multiLineColorList[i],
        })),
      };
      if (!fetchIncidentsRsp2?.error) {
        const chartHeatMatrixSeries = {
          axisData: {
            xName: timeLabel, yName: "", toolTipUnit: xAXIS_TOOLTIP_UNIT_IN_PERCENT,
            data: rsp2FirstTimes.map(m => axixDateformat(m.key)),
            tooltipData: rsp2FirstTimes.map(m => axixtooltipFormat(m.key)),
          },
          seriesValue: fetchIncidentsRsp2.apiresponse.map((outcome) => ({
            name: outcome?.locationName,
            locationId: outcome?.locationId,
            data: outcome?.ParkingAvailable_Outcome.map(m => toPercent(m.value, parseInt(outcome.locationParkingCapacity))),
          })),
        };
        setChartDataSetsForOA([chartUniLineSeries, chartBarSeries, chartHeatMatrixSeries, chartMultiLineSeries]);
      } else {
        setChartDataSetsForOA([chartUniLineSeries, chartBarSeries]);
      }
    } else {
      setChartDataSetsForOA([chartUniLineSeries, chartBarSeries]);
    }
    setDataMatrixForOAApps(parkingDataMatrix);
  }

  async function getPeopleCountEEWidgetMatrixDataDispaly() {
    const timeLabel = getTimeLabelWithTimezone();

    let contentFilter = {
      accountname: encryptParams?.accountname,
      tagid: encryptParams?.tagid,
      fixedinterval: encryptParams?.histogram_fixedinterval,
      appName: encryptParams?.eventfieldForPriority.split(".")[1],
      startDate: encryptParams?.startDate,
      endDate: encryptParams?.endDate,
      extendedendDate: encryptParams?.extendedendDate,
      aggregation: "latestininterval",
      tagList: encryptParams?.tagIdListEE
    };
    let fetchIncidentsRsp;
    let inOutEEFetchResponse;
    try {
      fetchIncidentsRsp = await getParkingOccupancyWidgetMatrixData(contentFilter);
      if(renderedFrom !== "EventInfo"){
        contentFilter.EntryExitkind = "sumininterval";
        inOutEEFetchResponse = await getInOutCountForEEAppsWidgetMatrixData(contentFilter);
      }
    } catch (err) {
      setWidgetError("Failed to load people count data. Please try again.");
      return;
    }
    if (fetchIncidentsRsp?.error) {
      setWidgetError("Failed to load people count data. Please try again.");
      return;
    }
    if (!fetchIncidentsRsp?.error) {
    const uniSeriesList =  fetchIncidentsRsp?.apiresponse?.[0]?.ParkingAvailable_Outcome?.map((_, i) =>
        fetchIncidentsRsp.apiresponse.reduce((sum, loc) => sum + (loc?.ParkingAvailable_Outcome?.[i]?.value?.latest?.value ?? 0), 0)
      );

       let chartUniLineSeries = {
        axisData: {"xName": timeLabel, "yName":safeAppImageName,
          "data": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: [
          {
            name:  safeAppImageName ,
            data: uniSeriesList,
            color: EventWidgetBlue
          }
        ]
      };
      if(renderedFrom === "EventInfo"){
        chartUniLineSeries.axisData.markerPoint = true;
        if(extendedstatus){
          const statValueNumber = parseSpaceDelimitedToken(stateCurrentValue, 0, parseInt);
          const data = chartUniLineSeries.seriesValue[0].data;
          data[data.length - 1] = statValueNumber + 1;
        }
        setChartDataSetsForEE([chartUniLineSeries]);
        setChartDataSets([chartUniLineSeries]);
        return;
      }


      let countwithnonZeros = fetchIncidentsRsp.finalDataOccupiedList.filter(f => f > 0).length;
      let avegaeoccupancy = countwithnonZeros > 0 ?
        fetchIncidentsRsp.finalDataOccupiedList.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / countwithnonZeros : 0;
      avegaeoccupancy = avegaeoccupancy == 0 || avegaeoccupancy == null || isNaN(avegaeoccupancy) ? 0 : parseInt(avegaeoccupancy);
      let maxpercentage = parseInt(Math.max(...fetchIncidentsRsp.finalDataOccupiedList));
      let avgPercentage = parseInt(avegaeoccupancy / fetchIncidentsRsp.parkingCapacity);
      let minpercentage = parseInt(Math.min(...fetchIncidentsRsp.finalDataOccupiedList));

      let parkingDataMatrix = [];
      parkingDataMatrix.push([
        { key: "Peak Occupancy", count: Math.max(...fetchIncidentsRsp.finalDataOccupiedList) }
      ]);
      parkingDataMatrix.push([
        { key: "Average Occupancy", count: avgPercentage },
        { key: "Max Occupancy", count: maxpercentage },
        { key: "Min Occupancy", count: minpercentage }
      ]);
      parkingDataMatrix.push([]);
      parkingDataMatrix.push([{ key: "Average Occupancy", count: avegaeoccupancy }]);

      parkingDataMatrix[1] = [
        { key: "In Count", count: inOutEEFetchResponse.totalInCount },
        { key: "Out Count", count: inOutEEFetchResponse.totalOutcount }
      ];
      
     
      let chartBarSeries = {
        axisData: {"xName": timeLabel, "yName":selectedCVAppName,
          "data": inOutEEFetchResponse?.dataResponse?.map((m) => axixDateformat(m.key)),
          "tooltipData": inOutEEFetchResponse?.dataResponse?.map((m) => axixtooltipFormat(m.key))},
        seriesValue: [
          {
            name:  "In" ,
            data: inOutEEFetchResponse?.dataResponse?.map(m => m.inCount),
            color: StatWidgetDarkBlue
          },
          {
            name:  "Out" ,
            data: inOutEEFetchResponse?.dataResponse?.map(m => m.outCount),
            color: EventWidgetBlue
          }
        ]
      };
      if(encryptParams.tagIdList.split(",").length > 0){
        let chartMultiLineSeries = {
        axisData: {"xName": timeLabel, "yName":selectedCVAppName,
          "data": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: fetchIncidentsRsp?.apiresponse.map((outcome, i) => { return {
            "name": outcome?.locationName,
            "locationId": outcome?.locationId,
            "data": outcome?.ParkingAvailable_Outcome.map(m => m.value.latest.value),
            "color": multiLineColorList[i]
          }
        })
      };
      //setChartDataSets([chartUniLineSeries,chartMultiLineSeries,chartMultiLineSeries]);

      contentFilter.fixedinterval = encryptParams?.eeFixedInterval;
       const fetchIncidentsRsp2 = await getParkingOccupancyWidgetMatrixData(contentFilter);
       if (!fetchIncidentsRsp2?.error) {
        let chartHeatMatrixSeries = {
        axisData: {"xName": timeLabel, "yName":"",
          "data": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: fetchIncidentsRsp2?.apiresponse.map((outcome, i) => { return {
            "name": outcome?.locationName,
            "locationId": outcome?.locationId,
            "data": outcome?.ParkingAvailable_Outcome.map(m => m.value.latest.value),
          }
        })
      };
      //setChartDataSets([chartUniLineSeries,chartHeatMatrixSeries,chartMultiLineSeries]);
      setChartDataSetsForEE([chartUniLineSeries,chartBarSeries,chartHeatMatrixSeries,chartMultiLineSeries])
      
       }
      }else{
        //setChartDataSets([chartUniLineSeries]);
        setChartDataSetsForEE([chartUniLineSeries, chartBarSeries])
      }
      setDataMatrixForEEApps(parkingDataMatrix);
      
    }
  }

  async function getPeopleCountOAWidgetMatrixDataDispaly(cvAppname) {
     const timeLabel = getTimeLabelWithTimezone();

     let contentFilter = {
       accountname: encryptParams?.accountname,
       tagid: encryptParams?.tagIdList?.split("=")[0]?.split("~")[1],
       fixedinterval: encryptParams?.histogram_fixedinterval,
       appName: cvAppname ? cvAppname : encryptParams?.eventfieldForPriority.split(".")[1],
       startDate: encryptParams?.startDate,
       endDate: encryptParams?.endDate,
       extendedendDate: encryptParams?.extendedendDate,
       aggregation: "sumwindowininterval",
       tagList: encryptParams?.tagIdListOA
     };
     let fetchIncidentsRsp;
     let fetchIncidentsMaxAvgRsp;
     try {
       fetchIncidentsRsp = await getCrowdDensityWidgetMatrixData(contentFilter);
       fetchIncidentsMaxAvgRsp = await getStatOaMaxAvgOfWidgetData(contentFilter);
     } catch (err) {
       setWidgetError("Failed to load people count data. Please try again.");
       return;
     }
     if (fetchIncidentsRsp?.error) {
       setWidgetError("Failed to load people count data. Please try again.");
       return;
     }
     if (!fetchIncidentsRsp?.error) {
       let maxItemlist = fetchIncidentsMaxAvgRsp?.apiresponse?.[0]?.ParkingAvailable_Outcome?.map((_, i) =>
         fetchIncidentsMaxAvgRsp.apiresponse.reduce((sum, loc) => sum + (loc?.ParkingAvailable_Outcome?.[i]?.max_value ?? 0), 0)
       );
       let avgItemlist = fetchIncidentsMaxAvgRsp?.apiresponse?.[0]?.ParkingAvailable_Outcome?.map((_, i) =>
         fetchIncidentsMaxAvgRsp.apiresponse.reduce((sum, loc) => sum + (loc?.ParkingAvailable_Outcome?.[i]?.avg_value ?? 0), 0)
       );

       let chartUniLineSeries = {
         axisData: {
           xName: timeLabel,
           yName: cvAppname ? safeAppImageName : "Crowd Count",
           data: fetchIncidentsMaxAvgRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
           tooltipData: fetchIncidentsMaxAvgRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key)),
           fullRecord: fetchIncidentsMaxAvgRsp
         },
         seriesValue: [
           {
             name: "Max " + safeAppImageName,
             data: maxItemlist,
             color: EventWidgetBlue,
             areaStyle: []
           },
           {
             name: "Avg " + safeAppImageName,
             data: avgItemlist,
             color: EventWidgetOrange,
             areaStyle: []
           }
         ]
       };

       if (renderedFrom === "EventInfo") {
         chartUniLineSeries.axisData.markerPoint = true;
         if (extendedstatus) {
           const statValueNumber = parseSpaceDelimitedToken(stateCurrentValue, 0, parseInt);
           chartUniLineSeries.seriesValue.forEach((series) => {
             series.data[series.data.length - 1] = statValueNumber;
           });
         }
         setChartDataSetsForOA([chartUniLineSeries]);
         setChartDataSets([chartUniLineSeries]);
         return;
       }

       if (renderedFrom !== "EventInfo") {
         let nonZeroNumberCount = fetchIncidentsRsp?.itemlist?.filter((f) => f > 0).length;
         let averageCrowd =
           nonZeroNumberCount > 0 ? fetchIncidentsRsp?.itemlist?.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / nonZeroNumberCount : 0;
         averageCrowd = averageCrowd == 0 || averageCrowd == null || isNaN(averageCrowd) ? 0 : parseInt(averageCrowd);

         let peakOccupancy = fetchIncidentsRsp?.itemlist?.length > 0 ? Math.max(...fetchIncidentsRsp?.itemlist) : 0;

         let crowdDataMatrix = [];
         crowdDataMatrix.push([{ key: "Peak Occupancy", count: peakOccupancy }]);
         crowdDataMatrix.push([]);
         crowdDataMatrix.push([{ key: "Average Occupancy", count: averageCrowd }]);

         if (encryptParams.tagIdList.split(",").length > 1) {
           let chartMultiLineSeries = {
             axisData: {
               xName: timeLabel,
               yName: cvAppname ? selectedCVAppName : "Crowd Count",
               data: fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
               tooltipData: fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key)),
               fullRecord: fetchIncidentsRsp
             },
             seriesValue: fetchIncidentsRsp?.apiresponse.map((outcome, i) => {
               return {
                 name: outcome?.locationName,
                 locationId: outcome?.locationId,
                 data: outcome?.ParkingAvailable_Outcome.map((m) => m.value),
                 color: multiLineColorList[i]
               };
             })
           };

           contentFilter.fixedinterval = encryptParams?.eeFixedInterval;
           const fetchIncidentsRsp2 = await getCrowdDensityWidgetMatrixData(contentFilter);
           if (!fetchIncidentsRsp2?.error) {
             let chartHeatMatrixSeries = {
               axisData: {
                 xName: timeLabel,
                 yName: "",
                 data: fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
                 tooltipData: fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key)),
                 fullRecord: fetchIncidentsRsp2
               },
               seriesValue: fetchIncidentsRsp2?.apiresponse.map((outcome, i) => {
                 return {
                   name: outcome?.locationName,
                   locationId: outcome?.locationId,
                   data: outcome?.ParkingAvailable_Outcome.map((m) => m.value)
                 };
               })
             };
             setChartDataSetsForOA([chartUniLineSeries, chartHeatMatrixSeries, chartMultiLineSeries]);
             //setChartDataSets([chartUniLineSeries,chartMultiLineSeries,chartMultiLineSeries]);
           }
         } else {
           setChartDataSetsForOA([chartUniLineSeries]);
           //setChartDataSets([chartUniLineSeries])
         }
         setDataMatrixForOAApps(crowdDataMatrix);
       }
     }
  }

  async function getCrowdDensityWidgetMatrixDataDispaly(cvAppname) {
    const timeLabel = getTimeLabelWithTimezone();

    let contentFilter = {
      accountname: encryptParams?.accountname,
      tagid: encryptParams?.tagid,
      fixedinterval: encryptParams?.histogram_fixedinterval,
      appName: cvAppname ? cvAppname : encryptParams?.eventfieldForPriority.split(".")[1],
      startDate: encryptParams?.startDate,
      endDate: encryptParams?.endDate,
      extendedendDate: encryptParams?.extendedendDate,
      aggregation: "sumwindowininterval",
      tagList: encryptParams?.tagIdList
    };
    let fetchIncidentsRsp;
    try {
      fetchIncidentsRsp = await getCrowdDensityWidgetMatrixData(contentFilter);
    } catch (err) {
      setWidgetError("Failed to load crowd density data. Please try again.");
      return;
    }
    if (fetchIncidentsRsp?.error) {
      setWidgetError("Failed to load crowd density data. Please try again.");
      return;
    }
    if (!fetchIncidentsRsp?.error) {
      let nonZeroNumberCount = fetchIncidentsRsp?.itemlist?.filter(f => f > 0).length;
      let averageCrowd = nonZeroNumberCount > 0 ? fetchIncidentsRsp?.itemlist?.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / nonZeroNumberCount : 0;
      averageCrowd = averageCrowd == 0 || averageCrowd == null || isNaN(averageCrowd) ? 0 : parseInt(averageCrowd);

      let peakOccupancy = fetchIncidentsRsp?.itemlist?.length > 0 ? Math.max(...fetchIncidentsRsp?.itemlist) : 0;

      let crowdDataMatrix = [];
      crowdDataMatrix.push([
        { key: "Peak Occupancy", count: peakOccupancy }
      ]);
      crowdDataMatrix.push([]);
      crowdDataMatrix.push([{ key: "Average Occupancy", count: averageCrowd }]);


      let chartUniLineSeries = {
        axisData: {"xName": timeLabel, "yName":cvAppname ? safeAppImageName :"Crowd Count",
          "data": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: [
          {
            name: cvAppname ? safeAppImageName : "Crowd Count",
            data: fetchIncidentsRsp?.itemlist,
            color: EventWidgetBlue
          }
        ]
      };

      if(encryptParams.tagIdList.split(",").length > 1){
        let chartMultiLineSeries = {
        axisData: {"xName": timeLabel, "yName":cvAppname ?  selectedCVAppName : "Crowd Count",
          "data": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: fetchIncidentsRsp?.apiresponse.map((outcome, i) => { return {
            "name": outcome?.locationName,
            "locationId": outcome?.locationId,
            "data": outcome?.ParkingAvailable_Outcome.map(m => m.value),
            "color": multiLineColorList[i]
          }
        })
      };
      //setChartDataSets([chartUniLineSeries,chartMultiLineSeries,chartMultiLineSeries]);

      contentFilter.fixedinterval = encryptParams?.eeFixedInterval;
       const fetchIncidentsRsp2 = await getCrowdDensityWidgetMatrixData(contentFilter);
       if (!fetchIncidentsRsp2?.error) {
        let chartHeatMatrixSeries = {
        axisData: {"xName": timeLabel, "yName":cvAppname ? selectedCVAppName: "Crowd Count",
          "data": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchIncidentsRsp2?.apiresponse[0]?.ParkingAvailable_Outcome.map((m) => axixtooltipFormat(m.key))},
        seriesValue: fetchIncidentsRsp2?.apiresponse.map((outcome, i) => { return {
            "name": outcome?.locationName,
            "locationId": outcome?.locationId,
            "data": outcome?.ParkingAvailable_Outcome.map(m => m.value),
          }
        })
      };
        setChartDataSets([chartUniLineSeries,chartHeatMatrixSeries,chartMultiLineSeries]);
       }
      }else{
        if(renderedFrom === "EventInfo"){
          chartUniLineSeries.axisData.markerPoint = true;
          if(extendedstatus){
            const statValueNumber = parseSpaceDelimitedToken(stateCurrentValue, 0, parseInt);
            const data = chartUniLineSeries.seriesValue[0].data;
            data[data.length - 1] = statValueNumber;
          }
          setChartDataSets([chartUniLineSeries]);
          return;
        }
        setChartDataSets([chartUniLineSeries]);
      }
      setDataMatrix(crowdDataMatrix);

    }
  }

  async function getDwellTimeWidgetMatrixDataDisplay() {
    const timeLabel = getTimeLabelWithTimezone();

    let contentFilter = {
      accountname: encryptParams?.accountname,
      tagid: encryptParams?.tagid,
      fixedinterval: encryptParams?.histogram_fixedinterval,
      appName: encryptParams?.eventfieldForPriority.split(".")[1],
      startDate: encryptParams?.startDate,
      endDate: encryptParams?.endDate,
      extendedendDate: encryptParams?.extendedendDate,
      tagList: encryptParams?.tagIdList
    };
    let fetchRsp;
    try {
      fetchRsp = await getDwellTimeWidgetMatrixData(contentFilter);
    } catch (err) {
      setWidgetError("Failed to load dwell time data. Please try again.");
      return;
    }
    if (fetchRsp?.error) {
      setWidgetError("Failed to load dwell time data. Please try again.");
      return;
    }
    if (!fetchRsp?.error) {
      const dwellList = fetchRsp?.finalDataDwellTimeList ?? [];
      const nonZeroCount = dwellList.filter(f => f > 0).length;
      const averageDwell = nonZeroCount > 0 ? dwellList.reduce((acc, val) => acc + val, 0) / nonZeroCount : 0;
      const maxList = Array.from({ length: fetchRsp?.apiresponse[0]?.DwellTime_Outcome?.length ?? 0 }, (_, i) => fetchRsp?.apiresponse.reduce((sum, n) => sum + (n?.DwellTime_Outcome?.[i]?.maxValue ?? 0), 0));
      const avgCount = averageDwell == 0 || isNaN(averageDwell) ? "0s" : toMin(averageDwell);
      const peakCount = toMin(dwellList.length > 0 ? Math.max(...dwellList) : 0);
      const maxCount = toMin(maxList.length > 0 ? Math.max(...maxList) : 0);
      const avgEntry = { key: "Average Dwell Time", count: avgCount };
      const peakEntry = { key: "Peak Dwell Time", count: peakCount };
      const maxEntry = { key: "Max Dwell Time", count: maxCount };

      const dwellDataMatrix = [
        [maxEntry],
        [avgEntry, peakEntry],
        [avgEntry],
      ];

      let chartUniScatterAvgSeries = {
        axisData: {
          "xName": timeLabel, "yName": "Dwell Time", "toolTipUnit": xAXIS_TOOLTIP_UNIT_IN_MINUTE,
          "data": fetchRsp?.apiresponse[0]?.DwellTime_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchRsp?.apiresponse[0]?.DwellTime_Outcome.map((m) => axixtooltipFormat(m.key))
        },
        seriesValue: [
          {
            name: "Dwell Time",
            data: fetchRsp?.apiresponse.map(n => n?.DwellTime_Outcome.map((m) => parseFloat((m.value).toFixed(1)))).flat(),
            color: EventWidgetBlue
          }
        ]
      };
      if(renderedFrom === "EventInfo"){
        chartUniScatterAvgSeries.axisData.markerPoint = true;
        if(extendedstatus){
          // index is stateCurrentValue.split(" ").length, which is always out of bounds — statValueNumber is always NaN
          const statValueNumber = parseSpaceDelimitedToken(stateCurrentValue, stateCurrentValue.split(" ").length, parseFloat);
          const data = chartUniScatterAvgSeries.seriesValue[0].data;
          data[data.length - 1] = statValueNumber;
        }
        setChartDataSets([chartUniScatterAvgSeries]);
        return;
      }

      let chartUniScatterMaxSeries = {
        axisData: {
          "xName": timeLabel, "yName": "Dwell Time", "toolTipUnit": xAXIS_TOOLTIP_UNIT_IN_MINUTE,
          "data": fetchRsp?.apiresponse[0]?.DwellTime_Outcome.map((m) => axixDateformat(m.key)),
          "tooltipData": fetchRsp?.apiresponse[0]?.DwellTime_Outcome.map((m) => axixtooltipFormat(m.key))
        },
        seriesValue: [
          {
            name: "Dwell Time",
            data: fetchRsp?.apiresponse.map(n => n?.DwellTime_Outcome.map((m) => parseFloat((m.maxValue).toFixed(1)))).flat(),
            color: EventWidgetBlue
          }
        ]
      };

      let c = fetchRsp?.apiresponse[0].DwellTime_Outcome.reduce((x,m) => x + m.value/60, 0);

      const dataDwellSeries = fetchRsp?.apiresponse.map(n => parseFloat((n?.DwellTime_Outcome.reduce((m, obj) => m + obj.value/60, 0)).toFixed(1)));
      let chartHBarSeries = {
        axisData: {
          "xName": timeLabel, "yName": "","toolTipUnit": xAXIS_TOOLTIP_UNIT_IN_MINUTE,
          "data": fetchRsp?.apiresponse.map((m) => m.locationName),
          "tooltipData": fetchRsp?.apiresponse.map((m) => m.locationName),
          "locationIdData": fetchRsp?.apiresponse.map((m) => m.locationId),
        },
        
        seriesValue: [
          {
            name: timeLabel,
            data: dataDwellSeries,
            color: EventWidgetBlue
          }
        ]
          
      };
      if (encryptParams.tagIdList.split(",").length > 1) {
        contentFilter.fixedinterval = encryptParams?.eeFixedInterval;
        const fetchRsp2 = await getDwellTimeWidgetMatrixData(contentFilter);
        if (!fetchRsp2?.error) {
          let chartHeatMatrixSeries = {
            axisData: {
              "xName": timeLabel, "yName": "",
              "data": fetchRsp2?.apiresponse[0]?.DwellTime_Outcome.map((m) => axixDateformat(m.key)),
              "tooltipData": fetchRsp2?.apiresponse[0]?.DwellTime_Outcome.map((m) => axixtooltipFormat(m.key))
            },
            seriesValue: fetchRsp2?.apiresponse.map((outcome) => ({
              "name": outcome?.locationName,
              "locationId": outcome?.locationId,
              "data": outcome?.DwellTime_Outcome.map(m => m.value),
            }))
          };
          setChartDataSets([chartUniScatterMaxSeries, chartUniScatterAvgSeries,chartHBarSeries, chartHeatMatrixSeries]);
        }
      } else {
        setChartDataSets([chartUniScatterMaxSeries, chartUniScatterAvgSeries, chartHBarSeries]);
      }
      setDataMatrix(dwellDataMatrix);
    }
  }

  const triggerCsvDownload = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const buildCsvContent = (headers, rows) =>
    [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");

  const locationNames = useMemo(
    () => 
      (encryptParams?.tagIdList ?? "").split(",").filter(Boolean).map((entry) => {
        const parsed = {};
        entry.split("=").forEach((segment) => {
          parsed[segment.split("~")[0]] = segment.split("~")[1];
        });
        return parsed.name ?? "";
      }),
    [encryptParams?.tagIdList]
  );

  const downloadWidgetCsv = (widget, chartDataSet) => {
    if (!chartDataSet) return;
    const { axisData, seriesValue } = chartDataSet;
    const safeName = widget.name.replace(/[\\/:*?"<>|]/g, "_");
    // --- CSV 1: chart series data ---
    const xLabels = axisData?.tooltipData?.length ? axisData.tooltipData : (axisData?.data || []);
    const chartHeaders = [axisData?.xName || "Time", ...(seriesValue?.map((s) => s.name) || [])];
    const chartRows = xLabels.map((label, idx) => [
      label,
      ...(seriesValue?.map((s) => {
        const v = s?.data?.[idx];
        if (v == null) return "";
        return axisData.toolTipUnit === " minute(s)" ? toMin(v) : v;
      }) || []),
    ]);
    const appnameHeading = `"Appname: ${selectedCVAppName}"\n`;
    const locationsHeading = `"Locations: ${locationNames.join(", ")}"\n`;
    triggerCsvDownload(appnameHeading + locationsHeading + buildCsvContent(chartHeaders, chartRows), `${safeName}.csv`);
  };

  const getLocationNameWithFullHeirarchy = (widget) => {
    let encryptedLocation = encryptParams.tagIdList;
    if (widget.name.includes("_ee")) {
      encryptedLocation = encryptParams.tagIdListEE;
    } else if (widget.name.includes("_oa")) {
      encryptedLocation = encryptParams.tagIdListOA;
    }
    return encryptedLocation
      .split(",")
      .filter(Boolean)
      .map((entry) => {
        const parsed = {};
        entry.split("=").forEach((segment) => {
          const [key, value] = segment.split("~");
          parsed[key] = value;
        });
        let locationHierarchy = getLocationHierarchy(sitesTagList, parsed.id ?? "");
        locationHierarchy = locationHierarchy.includes("/") ? locationHierarchy.replaceAll("/", "/ ") : locationHierarchy;
        return locationHierarchy
      });
  }

  return (
    <>
      {widgetList?.map((widget, i) => {
        if(fullWidgetList.widgets.some(f => f.name === widget.name) ){
        return (
            <div key={widget?.id || i} className={`${!showHideFilter ? "containerWithoutFilter" : "container"}`}>
              <div className="header">
                <div className="headerContentLeft">
                  <span>
                    {" "}
                    <Icon color="#000000" size="large" name={getIcon(getWidgetDisplayName(widget, selectedCVAppName), selectedCVAppName)} />
                    &nbsp;
                  </span>
                  {/* {getWidgetDisplayName(widget)} */}
                  <Tooltip placement="bottomLeft" autoAdjustOverflow="false" title={widget?.name}>
                    <span className="headerContentLeftSpanFontSize" style={{ font: "Verizon NHG eDS", fontWeight: "400" }}>{getWidgetDisplayName(widget, selectedCVAppName)}</span>
                  </Tooltip>
                  <Tooltip
                    overlayClassName="widget-location-tooltip"
                    placement="bottomLeft" 
                    title={
                      <div className="widget-location-tooltip-content">
                        {
                          <ul className="widget-location-tooltip-list">
                            {getLocationNameWithFullHeirarchy(widget)?.map((locationName, idx) => (
                              <li key={`${locationName}-${idx}`}>{locationName}</li>
                            ))}
                          </ul>
                        }
                      </div>
                    }
                  >
                    <span>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <Icon color="#000000" size="large" name="location" ariaHidden={true} />
                      &nbsp;
                    </span>
                  </Tooltip>

                  {renderedFrom == "Statistics" && dataMatrix.length > 0 && (
                    <>
                      {dataMatrix[i]?.map((item) => {
                        return (
                          <span key={item?.key} className="eventWidgetMatrixSpan eventWidgetMatrixSpan-desktop">
                            {item?.key + ": "}
                            <b className="eventWidgetMatrixSpanboldMinor">
                              {typeof item?.count === "number" && !Number.isInteger(item?.count) ? item?.count.toFixed(2) : item?.count}
                            </b>
                          </span>
                        );
                      })}
                    </>
                  )}
                </div>

                <div className="headerContentRightLabel">
                  
                    <div className="headerContentRightLabelDivContainer">
                     {(widget?.name.indexOf("_stat_oa") > -1 || widget?.name.indexOf("_stat_fa") > -1) && ( 
                      <div className="headerContentRightLabelDiv headerContentRightLabelDiv-desktop">
                      <span className="headerContentRightLabelDivContainerSpan">
                        Generated By FixedArea App
                      </span>
                      </div>)}
                       {widget?.name.indexOf("_stat_ee") > -1 && ( 
                       <div className="headerContentRightLabelDiv headerContentRightLabelDiv-desktop">
                       <span className="headerContentRightLabelDivContainerSpan">
                        Generated By EntryExit App
                      </span>
                      </div>)}
                      <Tooltip title="Download CSV">
                        <span className="headerContentRightLabelDivContainerCSVSpan" onClick={() => downloadWidgetCsv(widget, chartDataSets?.[i])}>
                          <Icon name="download" size="large" color="#070707" />
                        </span>
                      </Tooltip>
                      {/* Mobile info icon with tooltip */}
                  <Tooltip
                    placement="bottomRight"
                    overlayClassName="widget-info-tooltip-mobile"
                    title={
                      <div className="widget-info-tooltip-content">
                        {renderedFrom == "Statistics" && dataMatrix.length > 0 && (
                          <div className="tooltip-data-matrix">
                            {dataMatrix[i]?.map((item, idx) => (
                              <div key={idx} className="tooltip-matrix-item">
                                <span>{item?.key}:</span>
                                <strong>{typeof item?.count === "number" && !Number.isInteger(item?.count) ? item?.count.toFixed(2) : item?.count}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                        {(widget?.name.indexOf("_stat_oa") > -1 || widget?.name.indexOf("_stat_fa") > -1) && (
                          <div className="tooltip-generated-by">Generated By FixedArea App</div>
                        )}
                        {widget?.name.indexOf("_stat_ee") > -1 && (
                          <div className="tooltip-generated-by">Generated By EntryExit App</div>
                        )}
                      </div>
                    }
                  >
                    <span className="widget-info-icon-mobile">
                      <Icon name="info" size="medium" color="#000000" />
                    </span>
                  </Tooltip>
                    </div>
                  
                 
                </div>
              </div>
              <div className="content">

                {widgetError && (
                  <p style={{ color: "#d32f2f", fontSize: "13px", margin: "8px 0" }}>{widgetError}</p>
                )}

                {!widgetError && (statisticsCharts?.chart === CHART_TYPES.APACHE_CHARTS || encryptedHash?.length > 0) && (
                  <>
                    {renderedFrom != "EventInfo" && (
                      <div className="data-box">
                       
                        {statisticsCharts?.chart === CHART_TYPES.APACHE_CHARTS && (
                          <ApacheCharts
                            chartData={eventInfoResponse}
                            chartDataSet={chartDataSets?.[i]}
                            encryptpayload={encryptParams}
                            chartType={fullWidgetList.widgets.find((f) => f.name === widget.name)?.chartType}
                            showLegend={(chartDataSets?.[i]?.seriesValue?.length ?? 0) > 1}
                          ></ApacheCharts>
                        )}
                        
                        {(statisticsCharts?.chart === CHART_TYPES.KNOWI) && (
                          <KnowiChart
                            widget={widget}
                            divId={"knowi-div" + "PCount" + i}
                            widgetIndex={i}
                            encryptParams={encryptPayload}
                            encryptHash={encryptedHash}
                          />
                        )}
                      </div>
                    )}
                    {renderedFrom === "EventInfo" && (
                      <div className="data-box">
                        {chartDataSets.length == 0 && <p>Loading Widget</p>}
                        {chartDataSets.length > 0 && (
                          <ApacheCharts
                            chartData={eventInfoResponse}
                            chartDataSet={chartDataSets?.[i]}
                            encryptpayload={encryptParams}
                            chartType={fullWidgetList.widgets.find((f) => f.name === widget.name)?.chartType}
                            showLegend={(chartDataSets?.[i]?.seriesValue?.length ?? 0) > 1}
                          ></ApacheCharts>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
        );
      }
      })}
    </>
  );
};
export default WidgetComponent;
